import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { LatestContractDtoFragment, CareerPathEvaluation, CareerPathDto } from '../../../types'
import { useDateTime } from '../../../shared/useDateTime'
import { ProfileLabelListComponent } from '../ProfileLabelListComponent'
import { useAmountAndCurrency } from '../../../shared/useAmountAndCurrency'
import { CareerPathPopover } from '../../../components/custom_components/CarrerPathPopover/CareerPathPopover'
import PerText from '../../../components/custom_components/Text'
import PerSpacer from '../../../components/custom_components/Spacer'
import { filterCareerPathReferencesByLevel } from '../../appraisal/contract/careerPath/careerpath-util'
import { CompanyCarType, getCompanyCarType } from './utils/companyCarUtil'
import { isEmployeePartner } from './utils/contractDataUtil'
import { TimeZoneAndLocale } from '../../../types.custom'
import { useAuth } from '../../../context/auth-context'
import { round } from '../../../shared/numberUtil'

interface ContractComponentProps {
  currentContract: LatestContractDtoFragment
  isPublic?: boolean
  businessUnit?: { name: string; company: { name: string } }
  careerPathEvaluation?: CareerPathEvaluation | null
  isResigned?: boolean
  careerPath?: CareerPathDto[]
  timezone: TimeZoneAndLocale
  branchOfficeCode?: string | null
}

export const ContractComponent: FC<ContractComponentProps> = ({
  currentContract,
  businessUnit,
  careerPath,
  isPublic = false,
  careerPathEvaluation,
  isResigned = false,
  timezone,
  branchOfficeCode,
}) => {
  const { user } = useAuth()
  const { t } = useTranslation()
  const { formatDateShort } = useDateTime(timezone)
  const { formatCurrency } = useAmountAndCurrency(user.locale)
  const mt = 'l'
  const currentEmployeelevel = !!currentContract.employeeLevel ? currentContract.employeeLevel.level : undefined
  const currentContractType = currentContract.contractType.typeOfContract
  const currentCompanyCar = currentContract.companyCar

  let currentContractValidTo = currentContract.validTo ? currentContract.validTo : null
  if (!currentContractValidTo && currentContract.cancellationTo) {
    currentContractValidTo = currentContract.cancellationTo
  }
  const references =
    currentContract.employeeLevel && careerPath ? filterCareerPathReferencesByLevel(careerPath, currentContract.employeeLevel.level) : null
  const showFlexibleBenefit = typeof currentContract.flexibleBenefit === 'number' && currentContract.flexibleBenefit >= 0.0

  const contractDataVisible: boolean = isPublic
    ? currentContract.employeeLevel
      ? !isEmployeePartner(currentContract.employeeLevel.level)
      : false
    : true

  return (
    <>
      <PerText appearance="tile-heading" mb="xl">
        {isResigned ? t('profile.contract.headerResigned') : t('profile.contract.header')}
      </PerText>

      <ProfileLabelListComponent label={t('profile.contract.legalEntity')} value={currentContract.legalEntity.name} />
      <ProfileLabelListComponent label={t('profile.contract.virtualCompanyEntryDate')} value={formatDateShort(currentContract.validFrom)} />
      {currentContractValidTo && (
        <>
          <ProfileLabelListComponent label={t('profile.contract.endingDate')} value={formatDateShort(currentContractValidTo)} />
          {isPublic && currentContract.cancellationType && (
            <ProfileLabelListComponent
              label={t('profile.contract.endingReason')}
              value={t(`contract.cancellationType.${currentContract.cancellationType}`)}
            />
          )}
        </>
      )}
      <ProfileLabelListComponent label={t('profile.contract.type')} value={t(`contract.typeOfContract.${currentContractType}`)} />
      {currentCompanyCar &&
        [CompanyCarType.COMPANY_CAR, CompanyCarType.COMPANY_CAR_CAR_ALLOWANCE].includes(getCompanyCarType(currentCompanyCar.type)) && (
          <ProfileLabelListComponent
            label={t('profile.contract.companyCar.label')}
            value={
              <>
                <PerText display="inline">{t('global.yes')}</PerText>
                {!!currentCompanyCar.deduction && currentCompanyCar.deduction !== 0 && (
                  <>
                    <PerText mx="xxs" color="secondary" display="inline-block">
                      |
                    </PerText>
                    <PerText display="inline">
                      {`${t('profile.contract.companyCar.deduction')}: - ${formatCurrency(
                        currentCompanyCar.deduction,
                        currentCompanyCar.currency,
                        true,
                      )}`}
                    </PerText>
                  </>
                )}
              </>
            }
          />
        )}
      {showFlexibleBenefit && typeof currentContract.flexibleBenefit === 'number' && (
        <ProfileLabelListComponent
          label={t('profile.contract.flexibleBenefit.label')}
          value={
            currentContract.flexibleBenefit === 0
              ? t('profile.contract.flexibleBenefit.none')
              : formatCurrency(currentContract.flexibleBenefit, currentContract.currencyForPayments)
          }
        />
      )}

      {currentCompanyCar &&
        [CompanyCarType.CAR_ALLOWANCE, CompanyCarType.COMPANY_CAR_CAR_ALLOWANCE].includes(getCompanyCarType(currentCompanyCar.type)) && (
          <ProfileLabelListComponent
            label={t('profile.contract.carAllowance.label')}
            value={
              <>
                <PerText display="inline">{t('global.yes')}</PerText>
              </>
            }
          />
        )}

      <PerSpacer mt={mt}>
        {businessUnit && (
          <>
            {businessUnit.company && (
              <ProfileLabelListComponent label={t('profile.contract.virtualCompany')} value={businessUnit.company.name} />
            )}
            {businessUnit.name && <ProfileLabelListComponent label={t('profile.contract.businessUnit')} value={businessUnit.name} />}
          </>
        )}
        {branchOfficeCode && <ProfileLabelListComponent label={t('profile.contract.branchOffice')} value={branchOfficeCode} />}
        <ProfileLabelListComponent label={t('profile.contract.level')} value={t(`employee.contract.level.${currentEmployeelevel}`)} />
        {!!currentContract.contractGovernmentJobTitle && (
          <>
            <ProfileLabelListComponent
              label={t('profile.contract.currentTitle')}
              value={currentContract.contractGovernmentJobTitle.governmentJobTitle.title}
            />
            <ProfileLabelListComponent
              label={t('profile.contract.currentDepartment')}
              value={currentContract.contractGovernmentJobTitle.governmentJobTitle.department}
            />
            <ProfileLabelListComponent
              label={t('profile.contract.currentHiringOffice')}
              value={currentContract.contractGovernmentJobTitle.governmentJobTitle.hiringOffice.code}
            />
          </>
        )}
      </PerSpacer>

      {contractDataVisible && (
        <PerSpacer>
          <ProfileLabelListComponent
            label={t('profile.contract.currentAnnualSalary')}
            value={
              currentContract.annualSalary
                ? formatCurrency(currentContract.annualSalary.amount, currentContract.annualSalary.currency)
                : '-'
            }
          />
          {currentContract.isMonthlySalaryDisplayed && (
            <ProfileLabelListComponent
              label={t('profile.contract.currentMonthlySalary')}
              value={
                currentContract.annualSalary
                  ? formatCurrency(
                      round(currentContract.annualSalary.amount / currentContract.monthlySalaryMonthsInYear, 2),
                      currentContract.annualSalary.currency,
                    )
                  : '-'
              }
            />
          )}
          <ProfileLabelListComponent
            label={t('profile.contract.currentTargetBonus')}
            value={
              currentContract.targetBonus ? formatCurrency(currentContract.targetBonus.amount, currentContract.targetBonus.currency) : '-'
            }
          />
        </PerSpacer>
      )}

      {isPublic && !!careerPathEvaluation && references && (
        <PerSpacer mt={mt}>
          <ProfileLabelListComponent
            label={t('profile.contract.careerPath')}
            value={
              <CareerPathPopover
                careerPath={careerPathEvaluation}
                bonusReference={references.bonus}
                salaryReference={references.salary}
                currency={currentContract.currencyForPayments}
              />
            }
          />
        </PerSpacer>
      )}
    </>
  )
}
